// CareWay — product page behavior: variant switching + Add to Cart + recommendations.
// Real Shopify data throughout — no fake state. Progressive enhancement: the page
// works as a plain form post even if this script fails to load.

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.mainImage = this.querySelector('[data-product-gallery-main]');
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.count = this.querySelector('[data-gallery-count]');
    this.currentIndex = 0;

    this.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      this.select(this.thumbs.indexOf(thumb));
      return;
    }

    if (event.target.closest('[data-gallery-prev]')) this.select(this.currentIndex - 1);
    if (event.target.closest('[data-gallery-next]')) this.select(this.currentIndex + 1);
  }

  select(index) {
    if (!this.mainImage || this.thumbs.length === 0) return;
    const nextIndex = (index + this.thumbs.length) % this.thumbs.length;
    const thumb = this.thumbs[nextIndex];

    this.mainImage.removeAttribute('srcset');
    this.mainImage.src = thumb.dataset.mediaSrc;
    this.mainImage.alt = thumb.dataset.mediaAlt || this.mainImage.alt;

    this.thumbs.forEach((item, itemIndex) => {
      const isActive = itemIndex === nextIndex;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (this.count) this.count.textContent = `${nextIndex + 1} / ${this.thumbs.length}`;
    this.currentIndex = nextIndex;
  }

  selectByMediaId(mediaId) {
    const index = this.thumbs.findIndex((thumb) => thumb.dataset.mediaId === String(mediaId));
    if (index >= 0) {
      this.select(index);
      return true;
    }
    return false;
  }
}
customElements.define('product-gallery', ProductGallery);

class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this.form = this.closest('product-form')?.querySelector('form');
    this.addEventListener('click', this.onOptionClick.bind(this));
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  onOptionClick(event) {
    const btn = event.target.closest('.option-value');
    if (!btn || btn.disabled) return;
    const group = btn.closest('[data-option-index]');
    group.querySelectorAll('.option-value').forEach((b) => b.classList.remove('selected'));
    btn.classList.add('selected');
    const hiddenSelect = group.querySelector('select');
    if (hiddenSelect) hiddenSelect.value = btn.dataset.value;
    this.updateVariant();
  }

  onSelectChange(event) {
    if (!event.target.matches('[data-option-select]')) return;
    const group = event.target.closest('[data-option-index]');
    group.querySelectorAll('.option-value').forEach((b) => {
      b.classList.toggle('selected', b.dataset.value === event.target.value);
    });
    this.updateVariant();
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll('[data-option-select]')).map((el) => el.value);
  }

  updateVariant() {
    const selected = this.getSelectedOptions();
    const variants = JSON.parse(this.querySelector('[data-variants-json]').textContent);
    const variant = variants.find((v) => v.options.every((val, i) => val === selected[i]));

    const priceEl = document.querySelector('[data-product-price]');
    const idInput = document.querySelector('[data-variant-id-input]');
    const addBtn = document.querySelector('[data-add-to-cart]');
    const unavailableMsg = document.querySelector('[data-unavailable-message]');
    const dynamicCheckout = document.querySelector('[data-dynamic-checkout]');

    if (!variant) {
      if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Unavailable'; }
      if (unavailableMsg) unavailableMsg.style.display = 'block';
      if (dynamicCheckout) dynamicCheckout.style.display = 'none';
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (priceEl) priceEl.textContent = variant.price_formatted;

    if (variant.featured_media) {
      const gallery = document.querySelector('[data-product-gallery]');
      if (gallery && typeof gallery.selectByMediaId === 'function' && variant.featured_media_id && gallery.selectByMediaId(variant.featured_media_id)) {
        // The gallery handled the variant media.
      } else {
        const media = document.querySelector('[data-product-media]');
        if (media) {
          media.removeAttribute('srcset');
          media.src = variant.featured_media;
        }
      }
    }

    if (variant.available) {
      if (addBtn) { addBtn.disabled = false; addBtn.textContent = addBtn.dataset.defaultLabel; }
      if (unavailableMsg) unavailableMsg.style.display = 'none';
      if (dynamicCheckout) dynamicCheckout.style.display = '';
    } else {
      if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Sold Out'; }
      if (unavailableMsg) { unavailableMsg.textContent = 'This option is currently out of stock.'; unavailableMsg.style.display = 'block'; }
      if (dynamicCheckout) dynamicCheckout.style.display = 'none';
    }
  }
}
customElements.define('variant-selector', VariantSelector);

class ProductForm extends HTMLElement {
  constructor() {
    super();
    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', this.onSubmit.bind(this));
  }

  onSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const button = form.querySelector('[data-add-to-cart]');
    const errorEl = document.querySelector('[data-add-to-cart-error]');
    const formData = new FormData(form);
    // Ask Shopify to also render the cart-drawer section server-side, in
    // the SAME request — real Liquid/cart data, no client-side price or
    // variant-text duplication. See sections/cart-drawer.liquid.
    formData.append('sections', 'cart-drawer');
    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

    fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          // Shopify returns an error object with a `status` field on
          // failure (sold out, invalid variant, quantity unavailable,
          // etc.) — never open the drawer on this path.
          button.textContent = originalLabel;
          button.disabled = false;
          if (errorEl) {
            errorEl.textContent = data.description || data.message || 'Could not add to cart. Please try again.';
            errorEl.hidden = false;
          }
          return;
        }
        button.textContent = 'Added ✓';
        updateCartCount();
        if (data.sections && data.sections['cart-drawer'] && window.CareWayCartDrawer) {
          window.CareWayCartDrawer.renderAndOpen(data.sections['cart-drawer'], button);
        }
        setTimeout(() => { button.textContent = originalLabel; button.disabled = false; }, 1800);
      })
      .catch(() => {
        // Progressive enhancement fallback: submit the form normally
        form.submit();
      });
  }
}
customElements.define('product-form', ProductForm);

function updateCartCount() {
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      // [data-cart-count], not the shared `.cart-count` class — the
      // wishlist bubble reuses that same class for its own styling
      // (see header.liquid's data-wishlist-count span), so a plain
      // `.cart-count` selector matches it first and updates the wrong
      // badge.
      const badge = document.querySelector('[data-cart-count]');
      const cartIcon = document.querySelector('a.icon-btn[href*="/cart"]');
      if (cart.item_count > 0) {
        if (badge) {
          badge.textContent = cart.item_count;
        } else if (cartIcon) {
          const span = document.createElement('span');
          span.className = 'cart-count';
          span.setAttribute('data-cart-count', '');
          span.textContent = cart.item_count;
          cartIcon.appendChild(span);
        }
      } else if (badge) {
        badge.remove();
      }
    });
}

// Product recommendations — fetches Shopify's real recommendation results and
// injects them. No fake "You May Also Love" data.
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;
    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.querySelector('[data-recommendations-grid]');
        if (grid && grid.children.length > 0) {
          this.innerHTML = grid.innerHTML;
          this.closest('[data-recommendations-section]')?.removeAttribute('hidden');
        }
      })
      .catch(() => {});
  }
}
customElements.define('product-recommendations', ProductRecommendations);
